<template>
  <div class="login-page">
    <!-- Panel izquierdo: branding -->
    <div class="brand-panel">
      <div class="brand-content">
        <img src="/logo_blanco.png" alt="Gestion360 — Sistema de Administración Contable y Operativo" class="brand-logo">
        <p class="brand-tagline">Sistema de Administración<br>Contable y Operativo</p>
        <ul class="brand-features">
          <li><i class="bi bi-check2-circle"></i> Control de compras y requisiciones</li>
          <li><i class="bi bi-check2-circle"></i> Catálogos y configuración empresarial</li>
          <li><i class="bi bi-check2-circle"></i> Gestión de usuarios y permisos</li>
          <li><i class="bi bi-check2-circle"></i> Reportes y matriz de autorización</li>
        </ul>
      </div>
      <div class="brand-footer">
        <span class="brand-by">Desarrollado por</span>
        <a href="https://tangentemexico.com" target="_blank" rel="noopener" class="brand-link">
          Sistemas Tangente del Norte
        </a>
      </div>
    </div>

    <!-- Panel derecho: formulario -->
    <div class="form-panel">
      <div class="form-wrapper">
        <!-- Logo para mobile (oculto en desktop) -->
        <div class="mobile-brand">
          <img src="/icono_solido1.png" alt="Gestion360" class="mobile-isotipo">
          <h1 class="mobile-name">Gestion<span>360</span></h1>
        </div>

        <h2 class="form-title">Bienvenido</h2>
        <p class="form-subtitle">Ingresa tus credenciales para continuar</p>

        <!-- Mensaje de error -->
        <transition name="fade">
          <div v-if="error" class="alert-error">
            <i class="bi bi-exclamation-triangle-fill"></i>
            <span>{{ error }}</span>
          </div>
        </transition>

        <!-- Formulario -->
        <form @submit.prevent="handleLogin" class="login-form">
          <div class="field">
            <label for="UsuarioId">Usuario</label>
            <div class="field-input" :class="{ 'field-focus': focusUser }">
              <i class="bi bi-person"></i>
              <input
                ref="userInput"
                type="text"
                id="UsuarioId"
                v-model="UsuarioId"
                required
                autocomplete="username"
                placeholder="Tu usuario"
                @focus="focusUser = true"
                @blur="focusUser = false"
                @keydown.enter.prevent="$refs.passInput.focus()"
              >
            </div>
          </div>

          <div class="field">
            <label for="Password">Contrasena</label>
            <div class="field-input" :class="{ 'field-focus': focusPass }">
              <i class="bi bi-lock"></i>
              <input
                ref="passInput"
                :type="showPassword ? 'text' : 'password'"
                id="Password"
                v-model="Password"
                required
                autocomplete="current-password"
                placeholder="Tu contrasena"
                @focus="focusPass = true"
                @blur="focusPass = false"
              >
              <button type="button" class="toggle-password" @click="showPassword = !showPassword" tabindex="-1">
                <i :class="showPassword ? 'bi bi-eye-slash' : 'bi bi-eye'"></i>
              </button>
            </div>
          </div>

          <button type="submit" class="btn-login" :disabled="loading">
            <span v-if="loading" class="spinner"></span>
            <template v-else>
              <span>Iniciar Sesion</span>
              <i class="bi bi-arrow-right"></i>
            </template>
          </button>

          <router-link to="/recuperar-password" class="forgot-password-link">
            Olvide mi contrasena
          </router-link>
        </form>

        <!-- Footer mobile -->
        <div class="mobile-footer">
          <a href="https://tangentemexico.com" target="_blank">Sistemas Tangente del Norte</a>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import authService from '@/services/auth';
import appTituloService from '@/shared/services/appTitulo.service';

export default {
  name: 'LoginView',
  data() {
    return {
      UsuarioId: '',
      Password: '',
      loading: false,
      error: null,
      showPassword: false,
      focusUser: false,
      focusPass: false
    };
  },
  computed: {
    destino() {
      // Solo rutas internas del portal (evita mandar a otro sitio con ?redirect=//otro.com)
      const r = this.$route.query.redirect;
      return typeof r === 'string' && r.startsWith('/') && !r.startsWith('//') && !r.startsWith('/login') ? r : '/';
    }
  },
  created() {
    if (authService.isAuthenticated()) {
      this.$router.replace(this.destino);
    }
  },
  mounted() {
    this.$nextTick(() => {
      if (this.$refs.userInput) this.$refs.userInput.focus();
    });
  },
  methods: {
    async handleLogin() {
      this.loading = true;
      this.error = null;

      try {
        const response = await authService.login(this.UsuarioId, this.Password);
        if (response.Success) {
          appTituloService.refrescar();
          this.$router.replace(this.destino);
        } else {
          this.error = response.Message || 'Credenciales incorrectas';
        }
      } catch (error) {
        console.error('Error de login:', error);
        this.error = 'No se pudo conectar con el servidor';
      } finally {
        this.loading = false;
      }
    }
  }
};
</script>

<style scoped>
/* ========== Layout ========== */
.login-page {
  display: flex;
  min-height: 100vh;
  font-family: 'Outfit', 'Poppins', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
}

/* ========== Panel izquierdo: Branding ========== */
.brand-panel {
  flex: 0 0 44%;
  background: linear-gradient(160deg, #0a3d6b 0%, #0d5ca0 40%, #1272c4 100%);
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  position: relative;
  overflow: hidden;
}

.brand-panel::before {
  content: '';
  position: absolute;
  top: -20%;
  right: -20%;
  width: 60%;
  height: 60%;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(255,255,255,0.06) 0%, transparent 70%);
}

.brand-panel::after {
  content: '';
  position: absolute;
  bottom: -15%;
  left: -15%;
  width: 50%;
  height: 50%;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(255,255,255,0.04) 0%, transparent 70%);
}

.brand-content {
  text-align: center;
  z-index: 1;
}

.brand-logo {
  max-width: 320px;
  width: 80%;
}

.brand-tagline {
  color: rgba(255, 255, 255, 0.75);
  font-size: 1rem;
  font-weight: 500;
  margin: 1.25rem 0 1.75rem;
  line-height: 1.5;
}

.brand-features {
  list-style: none;
  padding: 0;
  margin: 0;
  text-align: left;
  display: inline-flex;
  flex-direction: column;
  gap: 0.6rem;
}

.brand-features li {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  color: rgba(255, 255, 255, 0.7);
  font-size: 0.85rem;
}

.brand-features li i {
  color: #68d391;
  font-size: 1rem;
  flex-shrink: 0;
}

.brand-footer {
  position: absolute;
  bottom: 2rem;
  z-index: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.2rem;
}

.brand-by {
  color: rgba(255, 255, 255, 0.3);
  font-size: 0.7rem;
}

.brand-link {
  color: rgba(255, 255, 255, 0.5);
  font-size: 0.78rem;
  text-decoration: none;
  transition: color 0.2s;
}

.brand-link:hover {
  color: rgba(255, 255, 255, 0.85);
}

/* ========== Panel derecho: Formulario ========== */
.form-panel {
  flex: 1;
  display: flex;
  justify-content: center;
  align-items: center;
  background: #f8fafc;
  padding: 2rem;
}

.form-wrapper {
  width: 100%;
  max-width: 380px;
}

.mobile-brand {
  display: none;
}

.form-title {
  font-size: 1.75rem;
  font-weight: 700;
  color: #1a202c;
  margin: 0 0 0.25rem 0;
}

.form-subtitle {
  color: #718096;
  font-size: 0.9rem;
  margin: 0 0 2rem 0;
}

/* ========== Alertas ========== */
.alert-error {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  background: #fff5f5;
  border: 1px solid #feb2b2;
  border-left: 3px solid #e53e3e;
  border-radius: 6px;
  color: #c53030;
  font-size: 0.85rem;
  margin-bottom: 1.5rem;
}

.alert-success {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  background: #f0fff4;
  border: 1px solid #9ae6b4;
  border-left: 3px solid #38a169;
  border-radius: 6px;
  color: #276749;
  font-size: 0.85rem;
  margin-bottom: 1.5rem;
}

.alert-error i,
.alert-success i {
  font-size: 1rem;
  flex-shrink: 0;
}

.fade-enter-active, .fade-leave-active {
  transition: opacity 0.3s ease;
}
.fade-enter-from, .fade-leave-to {
  opacity: 0;
}

/* ========== Campos del formulario ========== */
.login-form {
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
}

.field label {
  display: block;
  font-size: 0.8rem;
  font-weight: 600;
  color: #4a5568;
  margin-bottom: 0.4rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.field-input {
  display: flex;
  align-items: center;
  background: #ffffff;
  border: 1.5px solid #e2e8f0;
  border-radius: 10px;
  padding: 0 1rem;
  transition: all 0.2s ease;
}

.field-input.field-focus {
  border-color: #1272c4;
  box-shadow: 0 0 0 3px rgba(18, 114, 196, 0.1);
}

.field-input i {
  color: #a0aec0;
  font-size: 1rem;
  transition: color 0.2s;
}

.field-focus i {
  color: #1272c4;
}

.field-input input {
  flex: 1;
  border: none;
  outline: none;
  padding: 0.8rem 0.75rem;
  font-size: 0.95rem;
  color: #2d3748;
  background: transparent;
  font-family: inherit;
}

.field-input input::placeholder {
  color: #cbd5e0;
}

.toggle-password {
  background: none;
  border: none;
  cursor: pointer;
  padding: 0.25rem;
  color: #a0aec0;
  transition: color 0.2s;
}

.toggle-password:hover {
  color: #4a5568;
}

/* ========== Botones ========== */
.btn-login {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 0.5rem;
  width: 100%;
  padding: 0.85rem;
  background: linear-gradient(135deg, #0d5ca0, #1272c4);
  color: #ffffff;
  border: none;
  border-radius: 10px;
  font-size: 1rem;
  font-weight: 600;
  font-family: inherit;
  cursor: pointer;
  transition: all 0.25s ease;
  margin-top: 0.5rem;
}

.btn-login:hover:not(:disabled) {
  background: linear-gradient(135deg, #0a4d87, #0d5ca0);
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(13, 92, 160, 0.3);
}

.btn-login:active:not(:disabled) {
  transform: translateY(0);
}

.btn-login:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-login i {
  font-size: 1.1rem;
  transition: transform 0.2s;
}

.btn-login:hover:not(:disabled) i {
  transform: translateX(3px);
}

.forgot-password-link {
  align-self: flex-end;
  color: #0d5ca0;
  font-size: 0.85rem;
  font-weight: 500;
  text-decoration: none;
  margin-top: -0.5rem;
}

.forgot-password-link:hover {
  color: #1272c4;
  text-decoration: underline;
}

/* Spinner */
.spinner {
  width: 20px;
  height: 20px;
  border: 2.5px solid rgba(255, 255, 255, 0.3);
  border-radius: 50%;
  border-top-color: #ffffff;
  animation: spin 0.7s linear infinite;
}

.spinner-dark {
  border-color: rgba(0, 0, 0, 0.1);
  border-top-color: #4a5568;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.mobile-footer {
  display: none;
}

/* ========== Responsive ========== */
@media (max-width: 900px) {
  .brand-panel {
    display: none;
  }

  .form-panel {
    background: linear-gradient(160deg, #0a3d6b 0%, #0d5ca0 40%, #1272c4 100%);
    padding: 1.5rem;
  }

  .form-wrapper {
    background: #ffffff;
    padding: 2rem 1.5rem;
    border-radius: 16px;
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.15);
  }

  .mobile-brand {
    display: flex;
    flex-direction: column;
    align-items: center;
    margin-bottom: 2rem;
  }

  .mobile-isotipo {
    width: 60px;
    height: 60px;
    margin-bottom: 0.75rem;
  }

  .mobile-name {
    font-size: 1.75rem;
    font-weight: 700;
    color: #0d5ca0;
    margin: 0;
    letter-spacing: -0.5px;
  }

  .mobile-name span {
    color: #5eb8ff;
  }

  .form-title {
    text-align: center;
  }

  .form-subtitle {
    text-align: center;
  }

  .mobile-footer {
    display: block;
    text-align: center;
    margin-top: 2rem;
    padding-top: 1rem;
    border-top: 1px solid #e2e8f0;
  }

  .mobile-footer a {
    color: #a0aec0;
    font-size: 0.75rem;
    text-decoration: none;
  }
}

@media (max-width: 480px) {
  .form-panel {
    padding: 1rem;
  }

  .form-wrapper {
    padding: 1.5rem 1.25rem;
  }
}
</style>
